<template>
  <v-container id="po-items">
    <v-data-table
      :headers="headers"
      :items="items"
      dense
      :hide-default-footer="true"
      :disable-sort="true"
      :hide-default-header="true"
    >
      <!-- Custom Header -->
      <template v-slot:header="{ props }">
        <thead>
          <tr>
            <th
              v-for="header in props.headers"
              :key="header.text"
              class="custom-header"
              style=" border-bottom: thin solid rgb(0 0 0 / 0%);"
            >
              <p>{{ header.text }}</p>
            </th>
          </tr>
        </thead>
      </template>

      <!-- Body Rows -->
      <template v-slot:body="{ items }">
        <tr v-for="(item, index) in items" :key="index">
          <td v-if="item.expiry_date">
            {{ formatDate(item.expiry_date, dateFormat) }}
          </td>
          <td v-if="item.manufacture_date">
            {{ formatDate(item.manufacture_date, dateFormat) }}
          </td>
          <td class="wider-product">
            <v-text-field
              :value="getProductDisplay(item.product_code)"
              dense
              outlined
              placeholder="Product Code"
              @change="updateProductQuantity(item.product_code, index)"
              readonly
            ></v-text-field>
          </td>
          <td class="wider-descriptionnew">
            <v-text-field
              v-model="item.description"
              placeholder="Description"
              dense
              outlined
              clearable
            ></v-text-field>
          </td>
          <td class="wider-quantity">
            <v-text-field
              v-model.number="item.quantity"
              placeholder="Quantity"
              type="number"
              dense
              outlined
              clearable
              @blur="validateQuantity(item)"
            ></v-text-field>
          </td>
          <td>
            <v-text-field
              v-model="item.unit_price"
              placeholder="Unit Price"
              type="text"
              dense
              outlined
              clearable
              @keypress="validateDecimalInput($event)"
              @input="formatUnitPrice(item, $event)"
              @blur="formatUnitPriceOnBlur(item)"
            ></v-text-field>
          </td>
          <td>
            <v-text-field
              v-model="item.tax_percent"
              placeholder="VAT (%)"
              type="number"
              dense
              outlined
              readonly
            ></v-text-field>
          </td>
          <td>
            <v-text-field
              :value="formatDecimal(calculateTax(item))"
              placeholder="TAX (Rs.)"
              type="number"
              dense
              outlined
              readonly
            ></v-text-field>
          </td>
          <td>
            <v-text-field
              :value="formatDecimal(calculateEffectivePrice(item))"
              placeholder="Total (Rs.)"
              dense
              outlined
              readonly
            ></v-text-field>
          </td>
          <td class="pb-5">
            <v-icon
              size="30"
              color="red"
              @click="$emit('remove-item', index)"
              class="delete-icon"
            >
              mdi-delete
            </v-icon>
          </td>
        </tr>
      </template>
    </v-data-table>

    <!-- <v-row class="mt-0">
      <v-col class="text-left">
        <v-icon size="40" color="blue" @click="$emit('add-item')"
          >mdi-plus</v-icon
        >
      </v-col>
    </v-row> -->
  </v-container>
</template>

<script>
import { createNamespacedHelpers } from "vuex";
import { useDateFormatter } from "@/composables/useDateFormatter.js";
const { mapGetters: mapSettingsGetters } = createNamespacedHelpers("settings");
export default {
  computed: {
    ...mapSettingsGetters({
      dateFormat: "dateFormat",
    }),
  },
  setup() {
    const { formatDate } = useDateFormatter();
    return { formatDate };
  },
  name: "ItemsTable",
  props: {
    items: { type: Array, required: true },
    products: { type: Array, required: true },
    headers: {
      type: Array,
      default: () => [
        { text: "Product", value: "product_code" },
        { text: "Description", value: "description" },
        { text: "Quantity", value: "quantity" },
        { text: "Unit Price", value: "unit_price" },
        { text: "Tax (%)", value: "tax_percent" },
        { text: "Tax Amount", value: "tax" },
        { text: "Effective Price", value: "effective_price" },
        { text: "", value: "actions", sortable: false },
      ],
    },
    title: { type: String, default: "Items" },
  },

  watch: {
    unit_price(item) {
      this.calculateTax(item);
    },
  },

  methods: {
    getProductDisplay(productCode) {
      const product = this.products.find((p) => p.product_code === productCode);
      if (product) {
        return (
          product.product_code + (product.name ? " - " + product.name : "")
        );
      }
      return productCode || "";
    },
    calculateEffectivePrice(item) {
      const unitPrice = parseFloat(item.unit_price) || 0;
      const taxPercent = parseFloat(item.tax_percent) || 0;
      const quantity = parseFloat(item.quantity) || 0;

      const taxAmount = (unitPrice * taxPercent) / 100;
      return (unitPrice + taxAmount) * quantity;
    },

    calculateTax(item) {
      const unitPrice = parseFloat(item.unit_price) || 0;
      const taxPercent = parseFloat(item.tax_percent) || 0;
      const quantity = parseFloat(item.quantity) || 0;

      return (unitPrice * taxPercent * quantity) / 100;
    },

    // Format number to 2 decimal places
    formatDecimal(value) {
      const num = parseFloat(value || 0);
      return isNaN(num) ? "0.00" : num.toFixed(2);
    },

    // Validate decimal input on key press
    validateDecimalInput(event) {
      const key = event.key;
      const currentValue = event.target.value;

      // Allow: numbers, decimal point, backspace, delete, tab, escape, enter
      if (
        ["Backspace", "Delete", "Tab", "Escape", "Enter"].includes(key) ||
        // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
        (event.ctrlKey && ["a", "c", "v", "x"].includes(key))
      ) {
        return;
      }

      // Check if it's a number
      if (!/[\d.]/.test(key)) {
        event.preventDefault();
        return;
      }

      // Check for decimal point
      if (key === ".") {
        // If there's already a decimal point, prevent adding another
        if (currentValue.includes(".")) {
          event.preventDefault();
          return;
        }
        // If decimal point is at the beginning, add 0 before
        if (currentValue === "") {
          event.preventDefault();
          event.target.value = "0.";
          return;
        }
      }

      // If we already have a decimal point, check decimal places
      if (currentValue.includes(".")) {
        const decimalParts = currentValue.split(".");
        // If we already have 2 decimal places, prevent more input
        if (decimalParts[1] && decimalParts[1].length >= 2 && key !== ".") {
          event.preventDefault();
          return;
        }
      }
    },

    // Format unit price to 2 decimal places during input
    formatUnitPrice(item, event) {
      let value = event.target.value;

      // Allow empty value
      if (value === "") {
        item.unit_price = value;
        return;
      }

      // Remove any non-numeric characters except decimal point
      value = value.replace(/[^0-9.]/g, "");

      // Ensure only one decimal point
      const decimalParts = value.split(".");
      if (decimalParts.length > 2) {
        value = decimalParts[0] + "." + decimalParts.slice(1).join("");
      }

      // Limit to 2 decimal places in real-time
      if (decimalParts.length === 2) {
        value = decimalParts[0] + "." + decimalParts[1].substring(0, 2);
      }

      item.unit_price = value;
    },

    // Format unit price on blur to ensure 2 decimal places
    formatUnitPriceOnBlur(item) {
      if (item.unit_price && item.unit_price !== "") {
        let value = item.unit_price.toString();

        // Add .00 if no decimal places
        if (!value.includes(".")) {
          value = value + ".00";
        } else {
          const decimalParts = value.split(".");
          // Ensure exactly 2 decimal places
          if (decimalParts[1].length === 1) {
            value = decimalParts[0] + "." + decimalParts[1] + "0";
          } else if (decimalParts[1].length > 2) {
            value = decimalParts[0] + "." + decimalParts[1].substring(0, 2);
          }
        }

        const num = parseFloat(value);
        if (!isNaN(num)) {
          item.unit_price = num.toFixed(2);
        } else {
          item.unit_price = "";
        }
      } else if (item.unit_price === "") {
        item.unit_price = "";
      }
    },

    updateProductQuantity(productCode, index) {
      const selectedProduct = this.products.find(
        (product) => product.product_code === productCode
      );
      console.log(selectedProduct);
      if (selectedProduct) {
        const availableQuantity = selectedProduct.quantity || 0;
        this.$set(this.items, index, {
          ...this.items[index],
          quantity: availableQuantity,
          max_quantity: availableQuantity,
        });
      }
    },

    validateQuantity(item) {
      const selectedProduct = this.products.find(
        (product) => product.product_code === item.product_code
      );

      if (selectedProduct) {
        const availableQuantity = selectedProduct.quantity || 0;

        if (item.quantity > availableQuantity) {
          item.quantity = availableQuantity;
        } else if (item.quantity < 0) {
          item.quantity = 0;
        }
      }
    },
  },
};
</script>

<style scoped>
.v-text-field--outlined >>> fieldset {
  border-color: rgba(192, 0, 250, 0.215);
  border-radius: 10px;
}
</style>
